import { Box, Drawer, DrawerContent, DrawerOverlay, Modal, ModalOverlay, ModalContent } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../../helpers/useIsMobile';
import PspCancelModal from '../psp/PspCancelModal';
import PspConsentStep from './PspConsentStep';
import PspReviewStep from './PspReviewStep';
import PspSuccessStep from './PspSuccessStep';
import PspErrorStep from './PspErrorStep';
import PspAlreadyRegisteredStep from './PspAlreadyRegisteredStep';
import { MedicineService } from '../search/medicineService';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { getPspBenefit, PspProgramData } from '../../interfaces/medicine';
import { Patient, Prescriber } from '../../interfaces/users';
import { logger } from '../../helpers/logger';
import { toE164BrazilPhone } from '../../helpers/string';
import { APP_VIEWPORT_HEIGHT } from '../../helpers/embeddedViewport';

interface PspSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  programName?: string;
  optoutUrl?: string;
  partnerProgramUrl?: string;
  patientEmail?: string;
  patientPhone?: string;
  onConfirm?: (patientConsent: boolean, professionalConsent: boolean, email?: string, phone?: string) => void;
  onCancel?: () => void;
  medicineData?: any;
  prescription?: {
    idPrescricao: number;
    Paciente?: Patient;
    Prescritor?: Prescriber;
    idPrescritor?: number;
    CodigoValidacao?: string;
  };
  partnerId?: number;
  programId?: number;
  eanCode?: string;
  programData?: PspProgramData;
  initialStep?: 'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered';
  onStepChange?: (step: 'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered') => void;
}

type Step = 'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered';

const PspSidebar: React.FC<PspSidebarProps> = ({
  isOpen,
  onClose,
  programName = '',
  optoutUrl = '',
  partnerProgramUrl = '',
  patientEmail = '',
  patientPhone = '',
  onConfirm,
  onCancel,
  medicineData,
  prescription,
  partnerId,
  programId,
  eanCode,
  programData,
  initialStep,
  onStepChange,
}) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<Step>(() => {
    if (initialStep) return initialStep;
    if (programData?.registered) return 'alreadyRegistered';
    return 'consent';
  });
  const [patientConsent, setPatientConsent] = useState(false);
  const [professionalConsent, setProfessionalConsent] = useState(false);
  const [email, setEmail] = useState(patientEmail);
  const [phone, setPhone] = useState(patientPhone);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const previousIsOpenRef = useRef<boolean>(isOpen);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (!isOpen) {
      setEmail(patientEmail);
      setPhone(patientPhone);
      setIsLoading(false);
      setShowCancelModal(false);
    } else if (!wasOpen && isOpen) {
      let newStep: Step;
      if (initialStep) {
        newStep = initialStep;
      } else if (programData?.registered) {
        newStep = 'alreadyRegistered';
      } else {
        newStep = 'consent';
      }
      setStep(newStep);
      if (onStepChange) {
        onStepChange(newStep);
      }
      setPatientConsent(false);
      setProfessionalConsent(false);
      setEmail(patientEmail);
      setPhone(patientPhone);
      setIsLoading(false);
      setShowCancelModal(false);
    } else if (isOpen) {
      setEmail(patientEmail);
      setPhone(patientPhone);
    }
  }, [isOpen, patientEmail, patientPhone, initialStep, programData?.registered, onStepChange]);

  const handleConsentConfirm = async () => {
    const patientConsentOptional = programData?.program?.medicines?.[0]?.patientConsent === false;
    const doctorConsentOptional = programData?.program?.medicines?.[0]?.doctorConsent === false;

    const canProceed = (patientConsentOptional || patientConsent) && (doctorConsentOptional || professionalConsent);

    if (canProceed) {
      setStep('review');
      if (onStepChange) {
        onStepChange('review');
      }
    }

    await handleEventEmission('backoffice_campaign_psp_consent_click', {
      consentPaciente: patientConsent,
      consentMedico: professionalConsent,
      flow: 'consentimento',
    });
  };

  const handleBackFromReview = () => {
    setStep('consent');
    if (onStepChange) {
      onStepChange('consent');
    }
  };

  const handlePspPreEnrollment = async () => {
    await handleEventEmission('backoffice_campaign_psp_contato_review_click', {
      flow: 'contato paciente',
      continouPreCadastro: true,
    });

    if (!medicineData) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao verificar dados do medicamento:', { medicineData });
      if (onStepChange) {
        onStepChange('error');
      }

      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!prescription) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao verificar dados da prescrição: prescrição ausente');
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!partnerId || partnerId <= 0) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao verificar dados do parceiro:', { partnerId });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!programId || programId <= 0) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao verificar dados do programa:', { programId });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!eanCode) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao verificar dados do eanCode:', { eanCode });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    const patient = prescription.Paciente;
    const patientId = patient?.idPaciente;
    const patientName = patient?.Nome || '';
    const patientDocument = patient?.Documento || '';
    const patientBirthdate = patient?.Nascimento || '';
    const doctor = prescription.Prescritor;
    const doctorId = doctor?.idPrescritor || prescription.idPrescritor;
    const doctorName = doctor?.Nome || '';
    const doctorDocument = doctor?.Documento || '';
    const doctorLicenseState = doctor?.UFConselho || '';
    const doctorLicenseNumber = doctor?.Numero || '';
    const doctorLicenseType = doctor?.Conselho || '';
    const doctorEmail = doctor?.Email || '';

    if (!patientId || patientId <= 0 || !patientName || !patientDocument || !patientBirthdate) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao preencher dados do paciente:', {
        patientId,
        hasPatientName: !!patientName,
        hasPatientDocument: !!patientDocument,
        hasPatientBirthdate: !!patientBirthdate,
      });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (
      !doctorId ||
      doctorId <= 0 ||
      !doctorName ||
      !doctorDocument ||
      !doctorLicenseState ||
      !doctorLicenseNumber ||
      !doctorLicenseType
    ) {
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao preencher dados do médico:', {
        doctorId,
        hasDoctorName: !!doctorName,
        hasDoctorDocument: !!doctorDocument,
        hasDoctorLicenseState: !!doctorLicenseState,
        hasDoctorLicenseNumber: !!doctorLicenseNumber,
        hasDoctorLicenseType: !!doctorLicenseType,
      });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!email || email.trim() === '') {
      logger.error('[PSP - PreEnrollment] Erro ao preencher dados do email:', { email });
      setStep('error');
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    if (!phone || phone.trim() === '') {
      logger.error('[PSP - PreEnrollment] Erro ao preencher dados do telefone:', { hasPhone: !!phone });
      setStep('error');
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
      return;
    }

    setIsLoading(true);

    try {
      const data = await MedicineService.pspPreEnrollment({
        eanCode,
        programId,
        partnerId,
        patientId,
        doctorId,
        prescriptionId: prescription.idPrescricao,
        phone: toE164BrazilPhone(phone),
        email: email.trim(),
        patientConsent,
        doctorConsent: professionalConsent,
        patientName,
        patientDocument,
        patientBirthdate,
        doctorName,
        doctorDocument,
        doctorLicenseState,
        doctorLicenseNumber,
        doctorLicenseType,
        doctorEmail,
      });

      setIsLoading(false);
      if (!data?.isSuccess) {
        setStep('error');
        logger.error('[PSP - PreEnrollment] Erro ao criar pre-cadastro:', { error: data?.description });
        if (onStepChange) {
          onStepChange('error');
        }
        if (onConfirm) {
          onConfirm(patientConsent, professionalConsent, email, phone);
        }
        return;
      }

      if (medicineData?.IdMedicamento && programData) {
        const updatedProgramData = {
          ...programData,
          registered: true,
        };
        const { usePrescriptionStore } = await import('../../stores/prescriptionStore');
        usePrescriptionStore.getState().setMedicinePspProgramData(medicineData.IdMedicamento, updatedProgramData);
      }

      setStep('success');
      if (onStepChange) {
        onStepChange('success');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
    } catch (error: any) {
      setIsLoading(false);
      setStep('error');
      logger.error('[PSP - PreEnrollment] Erro ao criar pre-cadastro:', { error });
      if (onStepChange) {
        onStepChange('error');
      }
      if (onConfirm) {
        onConfirm(patientConsent, professionalConsent, email, phone);
      }
    }
  };

  const handleClose = () => {
    if (step === 'success' || step === 'alreadyRegistered' || step === 'error') {
      onClose();
      return;
    }
    setShowCancelModal(true);
    handleEventEmission('backoffice_campaign_psp_cancel_precadastro_view', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
    });
  };

  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    if (onCancel) {
      onCancel();
    }
    setStep('consent');
    onClose();

    handleEventEmission('backoffice_campaign_psp_cancela_precadastro_click', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
      continouPreCadastro: false,
    });
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    handleEventEmission('backoffice_campaign_psp_cancela_precadastro_click', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
      continouPreCadastro: true,
    });
  };

  const handleEventEmission = async (eventName: string, extraParams: any = {}) => {
    if (!prescription?.idPrescricao) return;

    await TrackerEventService.track(prescription.idPrescricao, eventName, {
      idPrescricao: prescription?.idPrescricao,
      codigoValidacao: prescription?.CodigoValidacao,
      idParceiro: partnerId,
      idMedico: prescription?.Prescritor?.idPrescritor,
      idPaciente: prescription?.Paciente?.idPaciente,
      idMedicamento: medicineData?.IdMedicamento,
      listaEans: medicineData?.EANs ?? medicineData?.ListaEANS ?? [],
      nomeDoPrograma: getPspBenefit(medicineData?.Beneficios)?.Nome,
      isMobile: isMobile,
      tipoDePrograma: 'PSP',
      ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
      ...extraParams,
    });
  };

  const renderContent = () => {
    switch (step) {
      case 'alreadyRegistered':
        return <PspAlreadyRegisteredStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'success':
        return <PspSuccessStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'error':
        return <PspErrorStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'review':
        return (
          <PspReviewStep
            email={email}
            phone={phone}
            isLoading={isLoading}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
            onBack={handleBackFromReview}
            onConfirm={handlePspPreEnrollment}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
          />
        );
      case 'consent':
      default:
        const patientConsentOptional = programData?.program?.medicines?.[0]?.patientConsent === false;
        const doctorConsentOptional = programData?.program?.medicines?.[0]?.doctorConsent === false;

        return (
          <PspConsentStep
            programName={programName}
            optoutUrl={optoutUrl}
            partnerProgramUrl={partnerProgramUrl}
            patientConsent={patientConsent}
            professionalConsent={professionalConsent}
            patientConsentOptional={patientConsentOptional}
            doctorConsentOptional={doctorConsentOptional}
            onPatientConsentChange={setPatientConsent}
            onProfessionalConsentChange={setProfessionalConsent}
            onConfirm={handleConsentConfirm}
            onClose={handleClose}
          />
        );
    }
  };

  return (
    <>
      <PspCancelModal isOpen={showCancelModal} onClose={handleCancelModalClose} onConfirm={handleCancelConfirm} />

      {isMobile ? (
        <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered={false}>
          <ModalOverlay />
          <ModalContent
            maxW="100vw"
            maxH={APP_VIEWPORT_HEIGHT}
            m={0}
            borderRadius={0}
            height={APP_VIEWPORT_HEIGHT}
            display="flex"
            flexDirection="column"
          >
            <Box height={APP_VIEWPORT_HEIGHT} display="flex" flexDirection="column" overflow="hidden">
              {renderContent()}
            </Box>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
          <DrawerOverlay />
          <DrawerContent>{renderContent()}</DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default PspSidebar;
